import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogContent, DialogTitle,
  IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { CloseOutlined, ShowChart, Search } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { blue, orange, pink, lime, grey, deepPurple, red, teal, cyan, yellow, green } from '@mui/material/colors';
import theme from '../theme';
import api from '../services/api';
import moment from 'moment';

const GRAPH_COLORS = [blue[500], orange[500], pink[500], lime[700], deepPurple[500], red[500], teal[500], cyan[500]];

const TIME_RANGES = [
  { label: '5m', seconds: 300 },
  { label: '15m', seconds: 900 },
  { label: '30m', seconds: 1800 },
  { label: '1h', seconds: 3600 },
  { label: '3h', seconds: 10800 },
];

const SEVERITY_MAP = {
  1: { label: 'Info', color: blue[300] },
  2: { label: 'Aviso', color: yellow[700] },
  3: { label: 'Medio', color: orange[500] },
  4: { label: 'Alto', color: orange[800] },
  5: { label: 'Desastre', color: red[600] },
};

const convertUnixToDateTime = (ts) => moment.unix(ts).format('HH:mm:ss');

function filterByTimeRange(data, rangeSeconds) {
  if (!data || data.length === 0) return data;
  const now = Math.floor(Date.now() / 1000);
  const cutoff = now - rangeSeconds;
  return data.filter(item => item.clock >= cutoff);
}

function formatValue(value, units) {
  const num = Number(value);
  if (isNaN(num)) return value;
  if (units === 'bps' || units === 'Bps') {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + ' Gbps';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + ' Mbps';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + ' Kbps';
    return num.toFixed(0) + ' bps';
  }
  if (units === '%') return num.toFixed(1) + '%';
  if (units === 'B') {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + ' GB';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + ' MB';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + ' KB';
    return num.toFixed(0) + ' B';
  }
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'G';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num % 1 !== 0 ? num.toFixed(2) : num.toString();
}

function ItemGraph({ itemid, itemName, units, codEmpresa, color, onRemove }) {
  const [data, setData] = useState([]);
  const [timeRange, setTimeRange] = useState('1h');

  useEffect(() => {
    const fetchData = () => {
      api.post('map/items-data', {
        cod_empresa: codEmpresa,
        itemid: itemid,
      }).then(res => {
        const tmp = res.data.map(d => ({
          ...d,
          value: Number(d.value),
          clock: Number(d.clock),
        })).sort((a, b) => a.clock - b.clock);
        setData(tmp);
      });
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [itemid, codEmpresa]);

  const rangeObj = TIME_RANGES.find(r => r.label === timeRange) || TIME_RANGES[3];
  const filtered = filterByTimeRange(data, rangeObj.seconds);

  const maxVal = filtered.length > 0 ? Math.max(...filtered.map(d => d.value)) : 0;
  let divisor = 1;
  let unitLabel = units || '';
  if (maxVal >= 1e9) { divisor = 1e9; unitLabel = 'G' + (units || ''); }
  else if (maxVal >= 1e6) { divisor = 1e6; unitLabel = 'M' + (units || ''); }
  else if (maxVal >= 1e3) { divisor = 1e3; unitLabel = 'K' + (units || ''); }

  const scaled = filtered.map(d => ({ ...d, value: d.value / divisor }));

  return (
    <Box sx={{ mb: 2, border: `1px solid ${grey[300]}`, borderRadius: 1, p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
          <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: 500 }}>
            {itemName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(e, val) => { if (val) setTimeRange(val); }}
            size="small"
            sx={{ height: 24 }}
          >
            {TIME_RANGES.map(r => (
              <ToggleButton key={r.label} value={r.label} sx={{ fontSize: 10, px: 0.8, py: 0 }}>
                {r.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton size="small" onClick={onRemove}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={scaled} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="clock" tickFormatter={convertUnixToDateTime} fontSize={10} />
          <YAxis unit={unitLabel} fontSize={10} />
          <Tooltip
            contentStyle={{ backgroundColor: grey[900], border: 'none' }}
            itemStyle={{ color: grey[100] }}
            labelFormatter={(v) => convertUnixToDateTime(v)}
            formatter={(v) => [v.toFixed(2) + ' ' + unitLabel]}
          />
          <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

function AlarmHistoryTab({ codEmpresa, hostid }) {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!codEmpresa || !hostid) return;
    setLoading(true);
    api.get(`/map/host-alarms/${codEmpresa}/${hostid}`)
      .then(res => setAlarms(res.data || []))
      .catch(() => setAlarms([]))
      .finally(() => setLoading(false));
  }, [codEmpresa, hostid]);

  if (loading) {
    return <Typography sx={{ p: 2 }}>Carregando alertas...</Typography>;
  }

  if (alarms.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
        <Typography color="text.secondary">Nenhum alerta nos ultimos 30 dias</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: '100%' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Severidade</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Ocorrencias</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Resolvido em</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alarms.map((alarm, idx) => {
            const sev = SEVERITY_MAP[alarm.severity] || { label: alarm.severity, color: grey[500] };
            return (
              <TableRow key={alarm.event_id || idx}>
                <TableCell>
                  <Chip label={sev.label} size="small"
                    sx={{ backgroundColor: sev.color, color: '#fff', fontWeight: 'bold', fontSize: 11 }}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography variant="body2" noWrap>{alarm.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={alarm.status === 'resolved' ? 'Resolvido' : 'Ativo'}
                    size="small"
                    sx={{
                      backgroundColor: alarm.status === 'resolved' ? green[100] : red[100],
                      color: alarm.status === 'resolved' ? green[800] : red[800],
                      fontSize: 11,
                    }}
                  />
                </TableCell>
                <TableCell>{alarm.occurrence_count || 1}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {alarm.clock ? moment(alarm.clock).format('DD/MM/YY HH:mm') : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {alarm.resolved_at ? moment(alarm.resolved_at).format('DD/MM/YY HH:mm') : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function HostItemsDialog({ open, codEmpresa, hostid, hostname, onClose }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [visibleCount, setVisibleCount] = useState(50);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open || !codEmpresa || !hostid) {
      setItems([]);
      setSelectedItems([]);
      setSearch('');
      setTab(0);
      setVisibleCount(50);
      return;
    }

    setLoading(true);
    api.get(`/map/host-items/${codEmpresa}/${hostid}`)
      .then(res => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, codEmpresa, hostid]);

  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      setVisibleCount(prev => prev + 50);
    }
  }, []);

  const filteredItems = items.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (item.description || '').toLowerCase().includes(q)
      || (item.name || '').toLowerCase().includes(q)
      || (item.key_ || '').toLowerCase().includes(q);
  });

  const toggleItem = (item) => {
    const exists = selectedItems.find(s => s.itemid === item.itemid);
    if (exists) {
      setSelectedItems(prev => prev.filter(s => s.itemid !== item.itemid));
    } else {
      setSelectedItems(prev => [...prev, item]);
    }
  };

  const isSelected = (itemid) => selectedItems.some(s => s.itemid === itemid);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl"
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
        <Box>
          <Typography variant="h6">{hostname || 'Host'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredItems.length} de {items.length} items | {selectedItems.length} selecionados
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseOutlined /></IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: `1px solid ${grey[300]}` }}>
        <Tab label="Items / Graficos" />
        <Tab label="Historico de Alertas" />
      </Tabs>

      <DialogContent sx={{ display: 'flex', gap: 2, p: 2, overflow: 'hidden' }}>
        {tab === 0 && (
          <>
            {/* Painel esquerdo: lista de items */}
            <Box sx={{ width: 380, minWidth: 380, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <TextField
                size="small"
                placeholder="Buscar por descricao..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setVisibleCount(50); }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: grey[500] }} /> }}
                sx={{ mb: 1 }}
                fullWidth
              />

              {/* Chips dos selecionados */}
              {selectedItems.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, maxHeight: 60, overflow: 'auto' }}>
                  {selectedItems.map((item, idx) => (
                    <Chip
                      key={item.itemid}
                      label={item.name}
                      size="small"
                      onDelete={() => toggleItem(item)}
                      sx={{ backgroundColor: GRAPH_COLORS[idx % GRAPH_COLORS.length] + '30', maxWidth: 180 }}
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 0.5 }} />

              <List dense ref={listRef} onScroll={handleScroll} sx={{ overflow: 'auto', flex: 1 }}>
                {loading ? (
                  <ListItem><ListItemText primary="Carregando items..." /></ListItem>
                ) : filteredItems.length === 0 ? (
                  <ListItem><ListItemText primary="Nenhum item encontrado" /></ListItem>
                ) : filteredItems.slice(0, visibleCount).map((item) => (
                  <ListItemButton key={item.itemid} onClick={() => toggleItem(item)} dense
                    selected={isSelected(item.itemid)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={isSelected(item.itemid)} size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                          {item.description && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 280, color: 'text.secondary' }}>
                              {item.description}
                            </Typography>
                          )}
                          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>{item.key_}</Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {formatValue(item.lastvalue, item.units)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* Painel direito: graficos */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {selectedItems.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
                  <Box textAlign="center">
                    <ShowChart sx={{ fontSize: 60, color: grey[400] }} />
                    <Typography color="text.secondary">Selecione items a esquerda para criar graficos</Typography>
                  </Box>
                </Box>
              ) : (
                selectedItems.map((item, idx) => (
                  <ItemGraph
                    key={item.itemid}
                    itemid={item.itemid}
                    itemName={item.name}
                    units={item.units}
                    codEmpresa={codEmpresa}
                    color={GRAPH_COLORS[idx % GRAPH_COLORS.length]}
                    onRemove={() => toggleItem(item)}
                  />
                ))
              )}
            </Box>
          </>
        )}

        {tab === 1 && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <AlarmHistoryTab codEmpresa={codEmpresa} hostid={hostid} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
